import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaSave } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';
import StaffStatus from './StaffStatus';

// Global Admin → SMTP Settings. Platform-level config for outbound login/verification
// email (donotreply@pipsqueaknetscan.com). Replaces the old .env-only setup so an
// admin can rotate the password (or switch to Microsoft 365 OAuth2) without a deploy.
function SmtpSettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    auth_mode: 'password',
    enabled: false,
    host: '',
    port: '',
    secure: false,
    email_name: '',
    has_email_pass: false,
    masked_email_pass: '',
    pass_expires_at: null,
    ms_tenant_id: '',
    ms_client_id: '',
    has_client_secret: false,
    masked_client_secret: ''
  });
  const [emailPass, setEmailPass] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const adminToken = localStorage.getItem('Admintoken');

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/s1/admin/smtp-settings', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load SMTP settings');
      if (data.settings) setSettings(data.settings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/smtp-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          authMode: settings.auth_mode,
          host: settings.host || '',
          port: settings.port || '',
          secure: settings.secure,
          emailName: settings.email_name || '',
          passExpiresAt: settings.pass_expires_at || null,
          tenantId: settings.ms_tenant_id || '',
          clientId: settings.ms_client_id || '',
          enabled: settings.enabled,
          // Only send secrets when the admin typed a new value (blank = keep existing).
          emailPass: emailPass.trim() || undefined,
          clientSecret: clientSecret.trim() || undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save SMTP settings');
      setSettings(data.settings);
      setEmailPass('');
      setClientSecret('');
      setMessage('SMTP settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const isOAuth = settings.auth_mode === 'oauth2';
  const isHealthy = settings.enabled && settings.host && (isOAuth
    ? settings.has_client_secret && settings.ms_tenant_id && settings.ms_client_id
    : settings.has_email_pass);

  return (
    <div className={isDarkMode ? 'text-light' : ''}>
      <StaffStatus on={isHealthy}>
        {isHealthy ? 'Enabled' : 'Disabled'}
      </StaffStatus>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Card className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Card.Body>
          <Row className="g-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Authentication Mode</Form.Label>
                <Form.Select
                  value={settings.auth_mode}
                  onChange={(e) => setSettings(prev => ({ ...prev, auth_mode: e.target.value }))}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                >
                  <option value="password">Password (mailbox app password)</option>
                  <option value="oauth2">Microsoft 365 OAuth2 (recommended)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>SMTP Host</Form.Label>
                <Form.Control
                  value={settings.host || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, host: e.target.value }))}
                  placeholder="smtp.office365.com"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Port</Form.Label>
                <Form.Control
                  value={settings.port || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, port: e.target.value }))}
                  placeholder="587"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="smtp-secure"
                label="TLS/SSL (465)"
                checked={!!settings.secure}
                onChange={(e) => setSettings(prev => ({ ...prev, secure: e.target.checked }))}
              />
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>Mailbox / Sender (EMAIL_NAME)</Form.Label>
                <Form.Control
                  value={settings.email_name || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, email_name: e.target.value }))}
                  placeholder="donotreply@pipsqueaknetscan.com"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>

            {!isOAuth && (
              <>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mailbox Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={emailPass}
                      onChange={(e) => setEmailPass(e.target.value)}
                      placeholder={settings.has_email_pass ? `Saved (${settings.masked_email_pass || '••••'}). Enter a new password to replace it.` : 'Enter the mailbox password'}
                      className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                    <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                      Stored encrypted server-side. Never returned to the browser.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Password Expiration Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={settings.pass_expires_at ? String(settings.pass_expires_at).slice(0, 10) : ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, pass_expires_at: e.target.value }))}
                      className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                    <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                      The health monitor alarms as this date approaches — set it whenever you rotate the password.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </>
            )}

            {isOAuth && (
              <>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Tenant ID</Form.Label>
                    <Form.Control
                      value={settings.ms_tenant_id || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, ms_tenant_id: e.target.value }))}
                      placeholder="Directory (tenant) ID"
                      className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Client ID</Form.Label>
                    <Form.Control
                      value={settings.ms_client_id || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, ms_client_id: e.target.value }))}
                      placeholder="Application (client) ID"
                      className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Client Secret</Form.Label>
                    <Form.Control
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder={settings.has_client_secret ? `Saved (${settings.masked_client_secret || '••••'}). Enter a new value to replace it.` : 'Client secret value'}
                      className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                    <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                      Stored encrypted server-side. Never returned to the browser.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={12}>
              <Form.Check
                type="switch"
                id="smtp-enabled"
                label="Use these settings instead of the server's .env fallback"
                checked={!!settings.enabled}
                onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              Save SMTP Settings
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default SmtpSettings;
